// DEITY FOCUS — tapping a figure makes it the focal point of the hall: the
// ◀ opens the deity's profile + past chats (resume any), ▶ opens its tool. The
// rotunda lifts so the floor medallion clears the chat box, the deity stands
// large on her podium on that circle, the room stays around her (just dimmed),
// and the chat sits at the bottom — her opening line, then text, dictation (🎙 → /api/ai/transcribe)
// and spoken replies (Kokoro via useAiVoice) once you've spoken to her.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ActivityIndicator, Animated, Easing, Image, KeyboardAvoidingView, Platform, Pressable, ScrollView, StyleSheet, Text, TextInput, View, useWindowDimensions } from 'react-native';
import { LinearGradient } from 'expo-linear-gradient';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Mic, MicOff, Phone, PhoneOff, Square, X } from 'lucide-react-native';
import { useSyqiChat } from '../olmps/useSyqiChat';
import { useAiVoice } from '../olmps/useAiVoice';
import type { NowStep } from './useCurrentStep';
import type { SeatView } from './useSelf';
import { GEAR_LABEL, SEAT_BY_ID, fullBodyFor, portraitFor } from './roster';
import { TIER_LABEL } from '../olmps/ascension';
import { getDeity } from '../olmps/deity-data';
import type { ProgressStats } from '../olmps/deity-progress';
import { DMT_TOOLS } from '../olmps/spectrum-tools';
import type { Conversation } from '../olmps/useSyqiChat';
import { EdgeTabs } from './EdgeTabs';
import { maladyFullFor } from './maladyCutouts';
import { deityOpener, deityStepContext } from './DeitySheet';
import { PANTHEON_PODIUM, PODIUM } from './pantheonLayout';
import { useDictation } from './useDictation';
import { useLiveVoice, AVATAR_MODE } from '../live/useLiveVoice';
import { AvatarVideo } from './AvatarVideo';
import { AvatarPuppet } from './AvatarPuppet';
import { FullBodyPuppet } from './FullBodyPuppet';
import { TalkingOverlay } from './TalkingOverlay';
import { LIVE_ASSETS } from './liveAssets';
import { FOCUS_CLIPS } from './focusClips';
import { DISPLAY_FONT, makeStyles, useV10 } from './theme';

export function DeityFocus({
  seatId,
  asMalady = false,
  seatView,
  floorX,
  floorY,
  podiumW,
  chatH,
  step,
  onClose,
  onOpenTool,
}: {
  seatId: string;
  /** The malady holding the seat was on the podium you tapped — bring it forward instead. */
  asMalady?: boolean;
  /** Screen point of the floor medallion's centre (the hall is lifted so it sits here). */
  floorX: number;
  floorY: number;
  /** Podium width: the medallion's own width, so it fills the circle. */
  podiumW: number;
  /** Height kept for the chat box at the bottom. */
  chatH: number;
  seatView?: SeatView;
  step: NowStep | null;
  onClose: () => void;
  onOpenTool: (seatId: string) => void;
}) {
  const t = useV10();
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  const { height: H } = useWindowDimensions();
  const seat = SEAT_BY_ID[seatId];
  const malady = asMalady && seatView ? { name: seatView.maladyName || 'Malady', id: seatView.maladyId } : null;
  const art = malady ? maladyFullFor(malady.id, seat.id) : fullBodyFor(seat.id);
  const who = malady ? malady.name.toUpperCase() : seat.name;
  // Chat always goes to the seat's deity; with a malady up, say so plainly.
  const opener = malady ? `${malady.name} holds ${seat.name}’s seat. Tell ${seat.name} where you’re stuck.` : deityOpener(seat.id, step);
  const chat = useSyqiChat({ toolId: seat.shellTool, endpoint: '/api/ai/deity' });
  const { messages, sending, send } = chat;
  const [profile, setProfile] = useState(false);
  const voice = useAiVoice({});
  const voiceOn = useRef(false);
  const [draft, setDraft] = useState('');
  const scroll = useRef<ScrollView>(null);

  const submit = useCallback(
    (text: string) => {
      const tx = text.trim();
      if (!tx) return;
      setDraft('');
      void send(tx, undefined, deityStepContext(step));
    },
    [send, step],
  );
  // Speak to her and she answers aloud.
  const dictation = useDictation(
    useCallback(
      (text: string) => {
        voiceOn.current = true;
        submit(text);
      },
      [submit],
    ),
  );

  const live = useLiveVoice({ toolId: seat.shellTool });
  const liveActive = live.state === 'connecting' || live.state === 'connected' || live.state === 'reconnecting';
  // Standard talking set (pre-keyed alpha loop + poster): the shared TalkingOverlay owns the call.
  const standardTalk = !!LIVE_ASSETS[seat.id]?.alphaWebm && !!LIVE_ASSETS[seat.id]?.poster;
  const [talkOpen, setTalkOpen] = useState(false);
  const startLive = () => {
    if (standardTalk) {
      void voice.stopSpeaking();
      dictation.cancel();
      voiceOn.current = false;
      setTalkOpen(true);
      return;
    }
    void voice.stopSpeaking();
    dictation.cancel();
    voiceOn.current = false;
    void live.start();
  };

  useEffect(() => {
    setTimeout(() => scroll.current?.scrollToEnd({ animated: true }), 60);
  }, [live.transcript]);

  const spoken = useRef(0);
  useEffect(() => {
    setTimeout(() => scroll.current?.scrollToEnd({ animated: true }), 60);
    const last = messages[messages.length - 1];
    if (voiceOn.current && !liveActive && last && last.role === 'assistant' && messages.length > spoken.current && !(last as { isError?: boolean }).isError) {
      spoken.current = messages.length;
      void voice.speak(last.content);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages.length]);
  useEffect(() => () => { void voice.stopSpeaking(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const a = useRef(new Animated.Value(0)).current;
  useEffect(() => {
    Animated.timing(a, { toValue: 1, duration: 380, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
  }, [a]);
  // Living portrait: slow breath while connected, soft gold glow while she speaks.
  const breath = useRef(new Animated.Value(0)).current;
  const glow = useRef(new Animated.Value(0)).current;
  const liveConnected = live.state === 'connected';
  const [avatarDrawn, setAvatarDrawn] = useState(false);
  useEffect(() => {
    if (!live.avatarStream && !live.puppetTrack) setAvatarDrawn(false);
  }, [live.avatarStream, live.puppetTrack]);
  // Full-body living set (web): always moving on this screen, mouth only while she talks.
  const focusSet = malady ? undefined : FOCUS_CLIPS[seat.id];
  const [focusDrawn, setFocusDrawn] = useState(false);
  const hideStill = focusSet ? focusDrawn : live.avatarState === 'on' && avatarDrawn;
  useEffect(() => {
    if (!liveConnected) {
      breath.setValue(0);
      return;
    }
    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(breath, { toValue: 1, duration: 2000, easing: Easing.inOut(Easing.sin), useNativeDriver: true }),
        Animated.timing(breath, { toValue: 0, duration: 2000, easing: Easing.inOut(Easing.sin), useNativeDriver: true }),
      ]),
    );
    loop.start();
    return () => loop.stop();
  }, [liveConnected, breath]);
  useEffect(() => {
    Animated.timing(glow, { toValue: liveConnected && live.deitySpeaking ? 0.55 : 0, duration: 250, useNativeDriver: true }).start();
  }, [liveConnected, live.deitySpeaking, glow]);
  const close = () => {
    live.end();
    voice.stopSpeaking();
    Animated.timing(a, { toValue: 0, duration: 220, easing: Easing.in(Easing.cubic), useNativeDriver: true }).start(() => onClose());
  };

  // Podium centred on the medallion at its width; she stands from it up to just under the header.
  const top = insets.top + 54;
  const podW = podiumW;
  const podH = (PODIUM.h / PODIUM.w) * podW;
  const feet = (PODIUM.feetY / PODIUM.w) * podW;
  const band = (PODIUM.bandY / PODIUM.w) * podW;
  const figH = Math.max(160, floorY - top + feet * 0.25);
  const figW = figH * (art?.aspect ?? 0.45);
  const stillW = focusSet ? figH * focusSet.aspect : figW; // living set: its own first frame, so the hand-off doesn't jump

  return (
    <Animated.View style={[StyleSheet.absoluteFill, { opacity: a, zIndex: 40 }]} pointerEvents="box-none">
      {/* The room stays, set well back in shadow. Tap it to leave. */}
      <Pressable onPress={close} style={[StyleSheet.absoluteFill, { backgroundColor: 'rgba(3,5,12,0.72)' }]} accessibilityLabel="Back to the Pantheon" />
      <Pressable onPress={close} style={[styles.close, { top: top - 4 }]} hitSlop={10} accessibilityLabel="Close">
        <X size={18} color={t.ivory} />
      </Pressable>

      <View pointerEvents="none" style={StyleSheet.absoluteFill}>
        <Animated.View
          style={{
            position: 'absolute',
            left: floorX - figW * 0.7,
            top: floorY + feet * 0.25 - figH * 0.95,
            width: figW * 1.4,
            height: figH * 0.9,
            borderRadius: figW,
            backgroundColor: 'rgba(214,180,106,0.5)',
            opacity: glow,
            ...(Platform.OS === 'web' ? ({ filter: 'blur(40px)' } as object) : { shadowColor: '#d6b46a', shadowOpacity: 1, shadowRadius: 50, shadowOffset: { width: 0, height: 0 } }),
          }}
        />
        <View style={{ position: 'absolute', left: floorX - podW / 2, top: floorY - feet, width: podW, height: podH }}>
          <Image source={PANTHEON_PODIUM} style={{ width: podW, height: podH }} resizeMode="stretch" />
          <View style={[styles.band, { top: band - podH * 0.16, height: podH * 0.32 }]}>
            <Text style={[styles.engraved, { fontSize: Math.min(podH * 0.24, (podW * 0.62) / (who.length * 0.8)) }]}>{who}</Text>
          </View>
        </View>
        {art ? (
          <Animated.Image
            source={focusSet ? focusSet.poster : art.src}
            resizeMode="contain"
            style={{ position: 'absolute', left: floorX - stillW / 2, top: floorY + feet * 0.25 - figH, width: stillW, height: figH, opacity: hideStill ? 0 : 1, transform: [{ scale: Animated.multiply(a.interpolate({ inputRange: [0, 1], outputRange: [0.94, 1] }), breath.interpolate({ inputRange: [0, 1], outputRange: [1, 1.012] })) }] }}
          />
        ) : null}
      </View>

      {live.avatarStream && art ? (
        <AvatarVideo
          stream={live.avatarStream}
          onFirstFrame={() => setAvatarDrawn(true)}
          style={{ left: floorX - (figW * 1.12) / 2, top: floorY + feet * 0.25 - figH - figH * 0.02, width: figW * 1.12, height: figW * 1.12 * 1.25 }}
        />
      ) : null}

      {focusSet && art ? (
        <FullBodyPuppet
          deity={seat.id}
          audioTrack={live.puppetTrack}
          deitySpeaking={live.deitySpeaking}
          userSpeaking={live.userSpeaking}
          onFirstFrame={() => setFocusDrawn(true)}
          left={floorX - (figH * focusSet.aspect) / 2}
          top={floorY + feet * 0.25 - figH}
          width={figH * focusSet.aspect}
          height={figH}
        />
      ) : null}

      {!focusSet && !standardTalk && AVATAR_MODE === 'puppet' && live.puppetTrack && art ? (
        <AvatarPuppet
          deity={seat.id}
          audioTrack={live.puppetTrack}
          speaking={live.deitySpeaking}
          onFirstFrame={() => setAvatarDrawn(true)}
          left={floorX - (figW * 1.12) / 2}
          top={floorY + feet * 0.25 - figH - figH * 0.02}
          width={figW * 1.12}
          height={figW * 1.12 * 1.25}
        />
      ) : null}

      {liveActive && art ? (
        <Pressable
          onPress={live.interrupt}
          accessibilityLabel={`Interrupt ${seat.name}`}
          style={{ position: 'absolute', left: floorX - figW / 2, top: floorY + feet * 0.25 - figH, width: figW, height: figH }}
        />
      ) : null}

      <KeyboardAvoidingView behavior={Platform.OS === 'ios' ? 'padding' : undefined} style={{ position: 'absolute', left: 0, right: 0, bottom: 0 }} pointerEvents="box-none">
        {/* Her opening line is her first bubble; the conversation follows it. */}
        <View style={{ maxHeight: H * 0.36 }}>
          <LinearGradient colors={['rgba(4,7,16,0)', 'rgba(4,7,16,0.82)']} locations={[0, 0.25]} style={StyleSheet.absoluteFill} pointerEvents="none" />
          <ScrollView ref={scroll} contentContainerStyle={styles.chat} keyboardShouldPersistTaps="handled">
            <Bubble who="deity" text={opener} />
            {messages.map((m, i) => (
              <Bubble key={i} who={m.role === 'user' ? 'user' : 'deity'} text={m.content} error={(m as { isError?: boolean }).isError} />
            ))}
            {live.transcript.map((l, i) => (
              <Bubble key={`live${i}`} who={l.role} text={l.text} />
            ))}
            {sending ? <ActivityIndicator color={t.gold} style={{ alignSelf: 'flex-start', margin: 4 }} /> : null}
          </ScrollView>
        </View>
        {dictation.error ? <Text style={styles.err}>{dictation.error}</Text> : null}
        {live.error ? <Text style={styles.err}>{live.error}</Text> : null}
        <View style={[styles.inputRow, { height: chatH, paddingBottom: insets.bottom }]}>
          <Pressable
            onPress={dictation.toggle}
            disabled={dictation.state === 'transcribing'}
            style={[styles.mic, dictation.state === 'recording' && styles.micOn]}
            accessibilityLabel={dictation.state === 'recording' ? 'Stop and send' : 'Dictate'}
          >
            {dictation.state === 'transcribing' ? <ActivityIndicator color={t.gold} /> : dictation.state === 'recording' ? <Square size={15} color={t.onGold} fill={t.onGold} /> : <Mic size={19} color={t.goldBright} />}
          </Pressable>
          {liveActive ? (
            <>
              <View style={styles.liveStatus}>
                <Text style={styles.liveStatusText}>
                  {live.state === 'connecting' ? 'Connecting…' : live.state === 'reconnecting' ? 'Reconnecting…' : live.avatarState === 'on' ? 'Live' : live.deitySpeaking ? 'Speaking' : live.muted ? 'Muted' : 'Listening'}
                </Text>
              </View>
              <Pressable onPress={live.toggleMute} style={[styles.liveBtn, live.muted && styles.micOn]} accessibilityLabel={live.muted ? 'Unmute' : 'Mute'}>
                <MicOff size={17} color={live.muted ? t.onGold : t.goldBright} />
              </Pressable>
              <Pressable onPress={live.end} style={styles.liveBtn} accessibilityLabel="End live voice">
                <PhoneOff size={17} color={t.goldBright} />
              </Pressable>
            </>
          ) : (
            <Pressable onPress={startLive} style={styles.liveBtn} accessibilityLabel={`Talk live with ${seat.name}`}>
              <Phone size={17} color={t.goldBright} />
            </Pressable>
          )}
          <TextInput
            value={draft}
            onChangeText={setDraft}
            placeholder={dictation.state === 'recording' ? 'Listening… tap ■ to send' : `Talk to ${seat.name}…`}
            placeholderTextColor="#8a8378"
            style={styles.input}
            onSubmitEditing={() => submit(draft)}
            returnKeyType="send"
          />
          <Pressable onPress={() => submit(draft)} style={styles.send} accessibilityLabel="Send">
            <Text style={styles.sendText}>➤</Text>
          </Pressable>
        </View>
      </KeyboardAvoidingView>
      {/* ◀ profile + past chats · ▶ the deity's tool. */}
      {!profile ? (
        <EdgeTabs
          left={() => setProfile(true)}
          right={() => {
            voice.stopSpeaking();
            onOpenTool(seat.id);
          }}
          rightTargetId="deityfocus.tool"
          labels={{ left: `${seat.name}: profile and past chats`, right: `Open ${seat.name}'s tool` }}
        />
      ) : (
        <DeityProfile
          seatId={seat.id}
          seatView={seatView}
          conversations={chat.conversations}
          loading={chat.loadingConversations}
          load={chat.loadConversations}
          current={chat.conversationId}
          onResume={(id) => {
            void chat.openConversation(id);
            setProfile(false);
          }}
          onNew={() => {
            void chat.startNewChat();
            setProfile(false);
          }}
          onBack={() => setProfile(false)}
        />
      )}
      {standardTalk ? <TalkingOverlay deityId={seat.id} displayName={seat.name} open={talkOpen} onClose={() => setTalkOpen(false)} /> : null}
    </Animated.View>
  );
}

/** The deity's profile — a screen to the LEFT of the conversation: it slides in
 *  from the left and its ▶ (right edge) slides back. Who the deity is, where you
 *  stand with it, what holding it feels like, its steps and principles, how the
 *  skill is proven, the maladies that take its seat, and every past conversation. */
function DeityProfile({ seatId, seatView, conversations, loading, load, current, onResume, onNew, onBack }: {
  seatId: string;
  seatView?: SeatView;
  conversations: Conversation[];
  loading: boolean;
  load: () => Promise<void> | void;
  current?: string;
  onResume: (id: string) => void;
  onNew: () => void;
  onBack: () => void;
}) {
  const t = useV10();
  const styles = useStyles();
  const insets = useSafeAreaInsets();
  const { width: W } = useWindowDimensions();
  const seat = SEAT_BY_ID[seatId];
  const info = getDeity(seat.id);
  const tool = DMT_TOOLS.find((x) => x.id === seat.id);
  useEffect(() => {
    void load();
  }, [load]);
  // Slide in from the left; leave the same way.
  const x = useRef(new Animated.Value(-W)).current;
  useEffect(() => {
    Animated.timing(x, { toValue: 0, duration: 320, easing: Easing.out(Easing.cubic), useNativeDriver: true }).start();
  }, [x]);
  const leave = (then: () => void) => Animated.timing(x, { toValue: -W, duration: 260, easing: Easing.in(Easing.cubic), useNativeDriver: true }).start(() => then());

  const portrait = portraitFor(seat.id);
  const st = seatView?.stats as Partial<ProgressStats> | undefined;
  const pct = st?.pct ?? 0;
  const when = (iso: string) => {
    const dt = new Date(iso);
    return Number.isNaN(dt.getTime()) ? '' : dt.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  };
  const Section = ({ title, children }: { title: string; children: React.ReactNode }) => (
    <View style={styles.section}>
      <Text style={[styles.profKicker, { textAlign: 'left' }]}>{title}</Text>
      {children}
    </View>
  );
  return (
    <Animated.View style={[StyleSheet.absoluteFill, styles.profile, { transform: [{ translateX: x }] }]}>
      <ScrollView contentContainerStyle={{ paddingTop: insets.top + 60, paddingBottom: insets.bottom + 40, paddingHorizontal: 26, gap: 6 }}>
        <View style={{ alignItems: 'center', gap: 4 }}>
          {portrait ? <Image source={portrait} style={styles.portrait} resizeMode="cover" /> : null}
          <Text style={styles.profName}>{seat.name}</Text>
          <Text style={styles.profKicker}>
            {GEAR_LABEL[seat.gear].name.toUpperCase()} · {TIER_LABEL[seat.tier]} · {GEAR_LABEL[seat.gear].question}
          </Text>
          <Text style={styles.profAbility}>{info.ability}</Text>
        </View>

        <Section title="WHERE YOU STAND">
          {seatView ? (
            <>
              <Text style={styles.profState}>{seatView.acquisition === 'acquired_unseated' ? 'Acquired · unseated' : seatView.ladderLine}</Text>
              <View style={styles.bar}>
                <View style={[styles.barFill, { width: `${Math.round(pct * 100)}%` }]} />
              </View>
              <Text style={styles.profBody}>
                {[
                  st?.days != null ? `${st.days} Days` : null,
                  st?.aims != null ? `${st.aims} Aims` : null,
                  st?.goals != null ? `${st.goals} Goals` : null,
                ]
                  .filter(Boolean)
                  .join(' · ') || 'Nothing banked yet.'}
              </Text>
              {st?.next ? <Text style={styles.profBody}>Next: {st.next}</Text> : null}
              <Text style={styles.profBody}>{seatView.seatLine}</Text>
            </>
          ) : (
            <Text style={styles.profBody}>No progress yet.</Text>
          )}
        </Section>

        <Section title="WHEN YOU HOLD IT">
          <Text style={styles.profQuote}>{info.have}</Text>
        </Section>

        <Section title="THE STEP · LIGHT · STANDARD · HEAVY">
          {info.steps.map((sx, i) => (
            <Text key={i} style={styles.profBody}>
              <Text style={styles.profStrong}>{['Light', 'Standard', 'Heavy'][i]}</Text> — {sx}
            </Text>
          ))}
        </Section>

        <Section title="PRINCIPLES">
          {info.principles.map((pr, i) => (
            <View key={i} style={styles.principle}>
              <Text style={styles.principleTitle}>{pr.title}</Text>
              <Text style={styles.profBody}>{pr.detail}</Text>
              <Text style={styles.principleFreq}>{pr.perWeek}× a week</Text>
            </View>
          ))}
        </Section>

        {tool?.certification ? (
          <Section title="HOW IT'S PROVEN">
            <Text style={styles.profBody}>{tool.certification}</Text>
          </Section>
        ) : null}

        {tool?.maladies.length ? (
          <Section title="WHAT TAKES ITS SEAT">
            <Text style={[styles.profBody, { color: t.maladyText }]}>{tool.maladies.map((m) => m.name).join(' · ')}</Text>
          </Section>
        ) : null}

        <Section title="PAST CHATS">
          <Pressable onPress={() => leave(onNew)} style={styles.newChat}>
            <Text style={styles.newChatText}>+ Start a new chat</Text>
          </Pressable>
          {loading ? <ActivityIndicator color={t.gold} style={{ marginTop: 8 }} /> : null}
          {!loading && conversations.length === 0 ? <Text style={styles.profEmpty}>No conversations yet.</Text> : null}
          {conversations.map((c) => (
            <Pressable key={c.id} onPress={() => leave(() => onResume(c.id))} style={[styles.convo, c.id === current && { borderColor: t.goldBright }]}>
              <Text style={styles.convoTitle} numberOfLines={1}>
                {c.title || 'Untitled conversation'}
              </Text>
              <Text style={styles.convoWhen}>{when(c.updated_at)}</Text>
            </Pressable>
          ))}
        </Section>
      </ScrollView>
      {/* Back to the conversation: the screen to the right. */}
      <EdgeTabs right={() => leave(onBack)} labels={{ right: `Back to ${seat.name}` }} />
    </Animated.View>
  );
}

function Bubble({ who, text, error }: { who: 'user' | 'deity'; text: string; error?: boolean }) {
  const t = useV10();
  const styles = useStyles();
  const mine = who === 'user';
  return (
    <View style={[styles.bubble, mine ? styles.mine : styles.theirs, error && { borderColor: t.lapsed }]}>
      <Text style={[styles.bubbleText, mine && { color: t.onGold }]}>{text}</Text>
    </View>
  );
}

const useStyles = makeStyles((t) =>
  StyleSheet.create({
    close: { position: 'absolute', right: 14, width: 36, height: 36, borderRadius: 18, alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(9,14,32,0.8)', borderWidth: 1, borderColor: 'rgba(214,180,106,0.6)', zIndex: 2 },
    band: { position: 'absolute', left: '12%', right: '12%', alignItems: 'center', justifyContent: 'center' },
    engraved: { fontFamily: DISPLAY_FONT, color: '#3d2f17', letterSpacing: 2, textShadowColor: 'rgba(255,240,205,0.6)', textShadowOffset: { width: 0, height: 0.8 }, textShadowRadius: 0.4 },
    chat: { paddingHorizontal: 14, paddingTop: 14, paddingBottom: 4, gap: 8 },
    bubble: { maxWidth: '88%', borderRadius: 18, paddingHorizontal: 14, paddingVertical: 9, borderWidth: 1 },
    theirs: { alignSelf: 'flex-start', backgroundColor: 'rgba(16,24,48,0.94)', borderColor: 'rgba(214,180,106,0.35)' },
    mine: { alignSelf: 'flex-end', backgroundColor: t.gold, borderColor: t.gold },
    bubbleText: { color: '#f3ead2', fontSize: 15, lineHeight: 21 },
    err: { color: t.lapsed, fontSize: 12, paddingHorizontal: 16 },
    inputRow: { flexDirection: 'row', gap: 8, paddingHorizontal: 14, alignItems: 'center', backgroundColor: 'rgba(4,7,16,0.82)' },
    mic: { width: 46, height: 46, borderRadius: 23, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: t.gold, backgroundColor: 'rgba(9,14,32,0.85)' },
    liveBtn: { width: 38, height: 46, borderRadius: 19, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: t.gold, backgroundColor: 'rgba(9,14,32,0.85)' },
    liveStatus: { paddingHorizontal: 4, justifyContent: 'center' },
    liveStatusText: { color: t.goldBright, fontSize: 11, fontWeight: '800', letterSpacing: 1 },
    micOn: { backgroundColor: t.gold },
    input: { flex: 1, height: 46, borderRadius: 23, backgroundColor: '#ffffff', paddingHorizontal: 16, color: '#111', fontSize: 15, borderWidth: 1, borderColor: t.gold },
    send: { width: 46, height: 46, borderRadius: 23, backgroundColor: t.gold, alignItems: 'center', justifyContent: 'center' },
    sendText: { color: t.onGold, fontSize: 18, fontWeight: '900' },
    profile: { backgroundColor: '#060a16', zIndex: 20 },
    portrait: { width: 104, height: 104, borderRadius: 52, borderWidth: 1.5, borderColor: t.gold, marginBottom: 6 },
    profName: { fontFamily: DISPLAY_FONT, color: '#f5efe0', fontSize: 30, letterSpacing: 3 },
    profKicker: { color: t.gold, fontSize: 11, fontWeight: '800', letterSpacing: 2.2, textAlign: 'center' },
    profAbility: { color: '#e9e2cf', fontFamily: DISPLAY_FONT, fontStyle: 'italic', fontSize: 15, textAlign: 'center' },
    profState: { color: t.goldBright, fontSize: 12.5, fontWeight: '700', textAlign: 'center' },
    profFoe: { color: t.maladyText, fontSize: 12.5, textAlign: 'center' },
    section: { gap: 6, marginTop: 20 },
    profBody: { color: '#e4dccb', fontSize: 14, lineHeight: 20 },
    profStrong: { color: t.goldBright, fontWeight: '700' },
    profQuote: { color: '#efe7d4', fontFamily: DISPLAY_FONT, fontStyle: 'italic', fontSize: 16, lineHeight: 23 },
    bar: { height: 6, borderRadius: 3, backgroundColor: 'rgba(214,180,106,0.18)', overflow: 'hidden', marginVertical: 2 },
    barFill: { height: 6, borderRadius: 3, backgroundColor: t.gold },
    principle: { borderLeftWidth: 2, borderLeftColor: 'rgba(214,180,106,0.5)', paddingLeft: 10, paddingVertical: 2, gap: 1 },
    principleTitle: { color: t.goldBright, fontFamily: DISPLAY_FONT, fontSize: 16 },
    principleFreq: { color: t.ivoryFaint, fontSize: 11.5 },
    profEmpty: { color: t.ivoryFaint, fontSize: 13, alignSelf: 'flex-start', marginTop: 4 },
    newChat: { alignSelf: 'stretch', borderWidth: 1, borderColor: t.gold, borderRadius: 14, paddingVertical: 11, alignItems: 'center', marginTop: 4 },
    newChatText: { color: t.goldBright, fontFamily: DISPLAY_FONT, fontSize: 15 },
    convo: { alignSelf: 'stretch', flexDirection: 'row', alignItems: 'center', gap: 10, borderWidth: 1, borderColor: 'rgba(214,180,106,0.3)', borderRadius: 14, paddingHorizontal: 14, paddingVertical: 12, backgroundColor: 'rgba(16,24,48,0.7)' },
    convoTitle: { flex: 1, color: '#f3ead2', fontSize: 14.5 },
    convoWhen: { color: t.ivoryFaint, fontSize: 12 },
  }),
);
